import { createContext, useContext, useEffect, useState } from "react";
import { Entrar } from "../telas/Entrar.js";
import { Desafio } from "../telas/Desafio.js";

export type Eu = {
  nome: string | null;
  email: string | null;
  estado: "completa" | "2fa_pendente";
  agenciaAtual: string | null;
  papel: string | null;
  permissoes: string[];
  agencias: { id: string; nome: string; papel: string }[];
  segundoFatorAtivo: boolean;
  segundoFatorExigido?: boolean;
};

export const ContextoDeSessao = createContext<Eu | null>(null);

export const useEu = (): Eu => {
  const eu = useContext(ContextoDeSessao);
  if (!eu) throw new Error("useEu fora do provedor de sessão.");
  return eu;
};
export const usePode = (permissao: string): boolean =>
  useContext(ContextoDeSessao)?.permissoes.includes(permissao) ?? false;

/**
 * Endereços que existem só ENQUANTO a pessoa está de fora.
 *
 * Depois de entrar, eles não têm tela no aplicativo: quem ficar parado em
 * `/entrar` com a sessão pronta vê "Página não encontrada" logo depois de
 * digitar a senha certa. Foi o que aconteceu, e ninguém percebeu porque quem
 * testa entra pela raiz.
 */
const CAMINHOS_DE_FORA = new Set(["/entrar", "/desafio", "/login"]);

/** Nada do app renderiza antes de a sessão existir. O `/auth/eu` chega na Onda 1. */
export function ProvedorDeSessao({ children }: { children: React.ReactNode }) {
  const [estado, setEstado] = useState<"carregando" | "fora" | "dentro">("carregando");
  const [eu, setEu] = useState<Eu | null>(null);

  const carregar = async () => {
    try {
      const agencia = localStorage.getItem("enotes:agencia");
      const r = await fetch("/auth/eu", { headers: agencia ? { "x-tenant-id": agencia } : {} });
      if (!r.ok) { setEstado("fora"); return; }
      const corpo = await r.json() as Eu;
      setEu(corpo);
      if (corpo.agenciaAtual) localStorage.setItem("enotes:agencia", corpo.agenciaAtual);

      // `replace` e não `push`: o endereço de entrada não deve ficar no
      // histórico, senão o botão de voltar leva a pessoa autenticada de volta
      // para uma tela que já não existe para ela.
      if (corpo.estado === "completa" && CAMINHOS_DE_FORA.has(window.location.pathname)) {
        window.history.replaceState({}, "", "/");
      }
      setEstado("dentro");
    } catch { setEstado("fora"); }
  };
  useEffect(() => { void carregar(); }, []);

  if (estado === "carregando") return <div className="enotes enotes-carregando" aria-busy="true" />;
  if (estado === "fora" || !eu) return <Entrar aoEntrar={() => void carregar()} />;
  // Sessão restrita NÃO entra no app: ela existe só para responder ao desafio
  // do segundo fator. Deixar passar daria acesso a quem só provou a senha.
  if (eu.estado === "2fa_pendente") return <Desafio aoEntrar={() => void carregar()} />;
  return <ContextoDeSessao.Provider value={eu}>{children}</ContextoDeSessao.Provider>;
}
